import React, { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { type PaymentRequest, type PaymentRequestPaymentMethodEvent, type Token } from '@stripe/stripe-js';
import { isSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useNavigation, useSearchParams } from 'react-router-dom';

import { ReactComponent as CryptoOptionIcon } from '@pkg/assets/icons/v2/crypto.svg';
import { ReactComponent as CryptoOptionIconMobile } from '@pkg/assets/icons/v2/crypto_btc.svg';
import { ReactComponent as PayAppleIcon } from '@pkg/assets/icons/v2/pay_apple.svg';
import { ReactComponent as PayGoogleIcon } from '@pkg/assets/icons/v2/pay_google.svg';
import { Checkbox, YatCryptoPaymentFlow } from '@pkg/components';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector, useLarge, useMobile, useTablet } from '@pkg/hooks';

import { Admin } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { Events } from '@app/client/common/structs/events/events';
import { CartStatus, type CheckoutMethodV2 } from '@app/client/common/structs/orders/cart';
import { type User, type UserAccount } from '@app/client/common/structs/user/user';
import { V2 } from '@app/client/resources/cart';
import { getOrder } from '@app/client/resources/orders';
import config from '@app/config';
import { decimals } from '@app/helpers';
import { NavigationState, RoutingSearchParams } from '@app/routes/types';
import { registerEvent } from '@app/store/actions/events';
import { setLoader } from '@app/store/actions/loader';
import { addNotification } from '@app/store/actions/notification';
import { getAddresses } from '@app/store/actions/partner-integrations';
import { getManualOrderItems, setCartItems, setOrderItems, setSecretKey } from '@app/store/actions/picker';
import { setUser } from '@app/store/actions/user';
import {
    getCurrentUserEmojis,
    setCurrentEmojiVisualizerUrl,
    setCurrentUserEmoji,
    setUserEmojis,
} from '@app/store/actions/user-emojis';
import { addAlert } from '@app/store/slices';
import { LoaderType } from '@app/store/types/loader';

import { CARD_DESKTOP_OPTIONS, CARD_MOBILE_OPTIONS, CARD_TABLET_OPTIONS } from './options';

const POLLING_INTERVAL = 2000;
const TERMS_LINK = config.termsLink;
const PRIVACY_LINK = config.privacyLink;

interface YatCheckoutFormProps {
    onSelectCrypto: (isCrypto: boolean) => void;
    switchFromCrypto: boolean;
}

export const YatCheckoutForm = ({ onSelectCrypto, switchFromCrypto }: YatCheckoutFormProps) => {
    const { t } = useTranslation(['yat_picker']);
    const stripe = useStripe();
    const elements = useElements();
    const { orderItems, orderId, savedSecretKey, cartStatus, secondsRemaining, requestInProgress } = useAppSelector(
        (rootState) => rootState.picker,
    );

    const userState = useAppSelector((rootState) => rootState.user);
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
    const [methodSwitchProcessing, setMethodSwitchProcessing] = useState(false);
    const [cryptoButtonDisabled, setCryptoButtonDisabled] = useState(true);
    const [optionForCrypto, setOptionForCrypto] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [walletKey, setWalletKey] = useState('');
    const [pollingId, setPollingId] = useState(-1);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const navigation = useNavigation();

    const { isTablet } = useTablet();
    const { isLarge } = useLarge();
    const { isMobile } = useMobile();

    const [searchParams] = useSearchParams();
    const locationState = useLocation().state;

    useEffect(() => {
        if (navigation.state !== NavigationState.Idle) {
            return;
        }
        const key = searchParams.get(RoutingSearchParams.PartnerKey);
        if (key) {
            dispatch(getAddresses());
        }
    }, [dispatch, navigation.state, searchParams]);

    useEffect(() => {
        // could come from either of the flows (1a&1b/2a&2b)
        const key = searchParams.get(RoutingSearchParams.PartnerKey) || locationState?.walletLinkCreation;
        setWalletKey(key);
    }, [locationState?.walletLinkCreation, searchParams]);

    const totalPrice = useMemo(() => {
        return orderItems
            .filter((orderItem) => !orderItem.marked_invalid_at)
            .map((orderItem) => orderItem.unit_price_in_cents / 100)
            .reduce((a, b) => a + b, 0);
    }, [orderItems]);

    const cardOption = useMemo(() => {
        if (isTablet) {
            return CARD_MOBILE_OPTIONS;
        }
        if (isLarge) {
            return CARD_TABLET_OPTIONS;
        }
        return CARD_DESKTOP_OPTIONS;
    }, [isTablet, isLarge]);

    const confirmCardPayment = useCallback(
        (clientSecret: string, token: Token, first_name: null | undefined, last_name: null | undefined) => {
            if (!stripe) {
                dispatch(addNotification({ message: t('invalid_stripe'), severity: 'error' }));
                return;
            }
            return stripe
                .confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: {
                            token: token.id,
                        },
                        billing_details: {
                            name: `${first_name} ${last_name}`,
                        },
                    },
                })
                .then(({ error, paymentIntent }) => {
                    if (error) {
                        dispatch(
                            addNotification({ message: error.message || t('checkout_failed'), severity: 'error' }),
                        );
                    }
                    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                        dispatch(addNotification({ message: t('yat_payment_intent_failed'), severity: 'error' }));
                    }
                })
                .catch((err) => {
                    dispatch(addNotification({ message: err, severity: 'error' }));
                });
        },
        [dispatch, stripe, t],
    );

    const confirmRequestPayment = useCallback(
        (clientSecret: string, ev: PaymentRequestPaymentMethodEvent) => {
            if (!stripe) {
                dispatch(addNotification({ message: t('invalid_stripe'), severity: 'error' }));
                return;
            }
            return stripe
                .confirmCardPayment(
                    clientSecret,
                    {
                        payment_method: ev.paymentMethod.id,
                    },
                    { handleActions: false },
                )
                .then(({ error, paymentIntent }) => {
                    if (error) {
                        ev.complete('fail');
                        dispatch(
                            addNotification({ message: error?.message || t('checkout_failed'), severity: 'error' }),
                        );
                    }
                    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                        ev.complete('fail');
                        dispatch(addNotification({ message: t('yat_payment_intent_failed'), severity: 'error' }));
                        return;
                    }
                    ev.complete('success');
                    return;
                })
                .catch(() => {
                    ev.complete('fail');
                    dispatch(addNotification({ message: t('yat_payment_failed'), severity: 'error' }));
                    return;
                });
        },
        [dispatch, stripe, t],
    );

    const handleBuyWithCreditCard = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!stripe || !elements || !userState || !orderItems.length || !orderId || !agreedToTerms) {
                return;
            }

            const cardNumberElement = elements.getElement(CardNumberElement);
            if (!cardNumberElement) {
                return;
            }

            setProcessing(true);
            stripe
                .createToken(cardNumberElement)
                .then(({ error, token }) => {
                    if (error) {
                        dispatch(addNotification({ message: error.message || t('checkout_failed') }));
                    }
                    if (!token) {
                        dispatch(addNotification({ message: t('yat_invalid_token') }));
                    }
                    return token;
                })
                .then((token) => {
                    if (token) {
                        // TODO Why are these null or undefined?
                        const first_name = userState.userAccount?.user.first_name;
                        const last_name = userState?.userAccount?.user.last_name;
                        if (savedSecretKey) {
                            return confirmCardPayment(savedSecretKey, token, first_name, last_name);
                        }
                        return apiFactory()
                            .call(Admin.Orders.getOrder, { id: orderId })
                            .then((r) => {
                                if (!r.ok && r.error.detail) {
                                    dispatch(addNotification({ message: r.error.detail }));
                                }
                                const clientSecret = r?.ok && r.value.payment_method_data.client_secret;

                                if (clientSecret) {
                                    dispatch(setSecretKey(clientSecret));
                                    return confirmCardPayment(clientSecret, token, first_name, last_name);
                                }
                            })
                            .catch((e) => {
                                dispatch(addNotification({ message: e || t('checkout_failed'), severity: 'error' }));
                            });
                    }
                })
                .then(() => {
                    setPollingId(0);
                })
                .catch((error) => {
                    // consume error
                    setProcessing(false);
                    dispatch(addAlert({ message: error }));
                    dispatch(getManualOrderItems(orderId));
                });
        },
        [
            stripe,
            elements,
            userState,
            orderItems,
            savedSecretKey,
            dispatch,
            t,
            confirmCardPayment,
            orderId,
            agreedToTerms,
        ],
    );

    useEffect(() => {
        const checkoutMethod: CheckoutMethodV2 = {
            method: 'Stripe',
            amount: totalPrice * 100,
        };
        if (totalPrice && orderId && cartStatus === CartStatus.Draft) {
            apiFactory()
                .call(V2.checkout, { value: checkoutMethod, id: orderId })
                .then((r) => {
                    if (!r.ok && r.error.detail) {
                        dispatch(addNotification({ message: r.error.detail }));
                    }
                    if (r.ok && r.value) {
                        dispatch(setCartItems(r.value));
                    }
                })
                .catch(() => {
                    dispatch(addNotification({ message: t('checkout_failed') }));
                });
        }
    }, [dispatch, t, totalPrice, orderId, cartStatus]);

    useEffect(() => {
        if (!stripe) {
            return;
        }
        const pr = stripe.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: {
                label: 'Total',
                amount: totalPrice * 100,
            },
        });

        pr.on('paymentmethod', (ev) => {
            if (!userState) {
                return;
            }

            setRequesting(true);
            if (savedSecretKey) {
                return confirmRequestPayment?.(savedSecretKey, ev)?.then(() => {
                    setPollingId(0);
                });
            }
            apiFactory()
                .call(Admin.Orders.getOrder, { id: orderId })
                .then((r) => {
                    if (!r.ok && r.error.detail) {
                        dispatch(addNotification({ message: r.error.detail }));
                    }

                    if (r.ok && r.value) {
                        const clientSecret = r.value.payment_method_data.client_secret;
                        dispatch(setSecretKey(clientSecret));
                        return confirmRequestPayment(clientSecret, ev);
                    }
                })
                .then(() => {
                    setPollingId(0);
                })
                .catch(() => {
                    dispatch(addNotification({ message: t('checkout_failed') }));
                });
        });

        pr.canMakePayment().then((res) => {
            if (!res) {
                return;
            }
            setPaymentRequest(pr);
        });
    }, [dispatch, userState, orderId, totalPrice, stripe, savedSecretKey, t, confirmRequestPayment]);

    const handlePaymentRequest = useCallback(() => {
        if (!paymentRequest || !agreedToTerms) {
            return;
        }
        paymentRequest.show();
    }, [paymentRequest, agreedToTerms]);

    const handleSwitch = useCallback(
        (crypto) => {
            onSelectCrypto(crypto);
            setOptionForCrypto(crypto);
        },
        [onSelectCrypto],
    );

    const handleBack = useCallback(() => {
        handleSwitch(false);

        setMethodSwitchProcessing(true);

        const checkoutMethod: CheckoutMethodV2 = {
            method: 'Stripe',
            amount: totalPrice * 100,
        };

        apiFactory()
            .call(V2.checkout, { value: checkoutMethod, id: orderId })
            .then((r) => {
                if (!r.ok && r.error.detail) {
                    dispatch(addNotification({ message: r.error.detail }));
                }
                if (r.ok && r.value) {
                    dispatch(setCartItems(r.value));
                }
            })
            .catch(() => {
                dispatch(addNotification({ message: t('checkout_failed') }));
            })
            .finally(() => {
                setMethodSwitchProcessing(false);
            });
    }, [totalPrice, dispatch, t, orderId, handleSwitch]);

    useEffect(() => {
        if (switchFromCrypto && optionForCrypto) {
            handleBack();
        }
    }, [switchFromCrypto, handleBack, optionForCrypto]);

    useEffect(() => {
        if (pollingId < 0) {
            return;
        }
        setTimeout(() => {
            apiFactory()
                .call(getOrder, { id: orderId })
                .then((r) => {
                    setPollingId((pollingId) => pollingId + 1);
                    if (!r.ok && r.error.detail) {
                        setProcessing(false);
                        dispatch(addAlert({ message: r.error.detail || 'Invalid Order Items' }));
                        return;
                    }
                    if (r.ok && r.value) {
                        if (r.value.status === 'Paid') {
                            setPaymentSuccess(true);
                            setPollingId(-1);
                            setRequesting(false);
                            setProcessing(false);
                        }

                        if (r.value.user) {
                            const user: User = r.value.user;
                            dispatch(setUser({ user } as UserAccount));
                        }
                    }
                })
                .catch(() => {
                    dispatch(addAlert({ message: 'Invalid Order Items' }));
                    setPollingId((pollingId) => pollingId + 1);
                });
        }, POLLING_INTERVAL);
    }, [orderId, pollingId, dispatch]);

    const emojisInCartList = useMemo(() => {
        return orderItems.map((orderItem) => orderItem.emoji_id).join(',');
    }, [orderItems]);

    useEffect(() => {
        if (!paymentSuccess) {
            return;
        }
        const validOrderItems = orderItems.filter((item) => !item.marked_invalid_at); //so we only display the actual purchased Yats on the confirmation screen

        const validFirstYat = validOrderItems?.[0]?.emoji_id;

        dispatch(setLoader({ open: true, type: LoaderType.YatV2Loader, source: t('launching_yat_machine') }));
        dispatch(setCurrentUserEmoji(validFirstYat));
        dispatch(setUserEmojis(validOrderItems.map((e) => e.emoji_id)));
        dispatch(setCurrentEmojiVisualizerUrl(validFirstYat));
        dispatch(setLoader({ open: false }));
        registerEvent(Events.purchased, { yat_unicode: emojisInCartList, payment_type: 'fiat', price: totalPrice });

        navigate(`/${validFirstYat}/success`, {
            state: {
                walletLinkCreation: walletKey,
                orderItemCount: validOrderItems.length,
            },
        });
        dispatch(getCurrentUserEmojis()).then((r) => {
            if (r) {
                dispatch(setOrderItems([]));
            }
        });
    }, [paymentSuccess, orderItems, t, dispatch, navigate, emojisInCartList, totalPrice, walletKey]);

    const brandIcon = useMemo(() => {
        if (isSafari) {
            return <PayAppleIcon className="yat-picker-checkout__sidebar-request-brand" />;
        } else {
            return <PayGoogleIcon className="yat-picker-checkout__sidebar-request-brand" />;
        }
    }, []);

    const buttonLoading = useMemo(() => {
        return requestInProgress || processing || secondsRemaining === null || secondsRemaining === undefined;
    }, [processing, secondsRemaining, requestInProgress]);

    useEffect(() => {
        //adding this further delay on the buttons so users can't switch between each payment method too quickly
        if (buttonLoading || methodSwitchProcessing) {
            // if it's loading set it immediately (because we're disabling the buttons delete too, so it needs to change instantly there
            setCryptoButtonDisabled(true);
        } else {
            //if it's coming out of loading add the delay
            setTimeout(() => {
                setCryptoButtonDisabled(false);
            }, 1000);
        }
    }, [buttonLoading, methodSwitchProcessing]);

    if (!optionForCrypto) {
        return (
            <div className="yat-picker-checkout__sidebar-vertical">
                {paymentRequest && (
                    <DepButton
                        onClick={handlePaymentRequest}
                        size="large"
                        display={DepButtonDisplay.block}
                        classes={`yat-picker-checkout__sidebar-request${isSafari ? ' apple' : ''}`}
                        disabled={!paymentRequest || !agreedToTerms}
                        loading={requesting}
                    >
                        {brandIcon}
                        <span>Pay</span>
                    </DepButton>
                )}
                <DepButton
                    size="large"
                    display={DepButtonDisplay.block}
                    classes="yat-picker-checkout__sidebar-crypto"
                    onClick={() => handleSwitch(true)}
                    disabled={!agreedToTerms || cryptoButtonDisabled}
                >
                    {isMobile ? <CryptoOptionIconMobile /> : <CryptoOptionIcon />}
                    <span>{t('pay_with_crypto')}</span>
                </DepButton>
                <div className="yat-picker-checkout__sidebar-divider">
                    <div className="hr" />
                    <span>{t('pay_with_credit_card')}</span>
                    <div className="hr" />
                </div>
                <form onSubmit={handleBuyWithCreditCard}>
                    <div className="yat-picker-checkout__sidebar-row">
                        <div className="yat-picker-checkout__sidebar-col">
                            <div className="yat-picker-checkout__sidebar-field">
                                <CardNumberElement
                                    options={{
                                        ...cardOption,
                                        ...{ placeholder: 'Credit Card Number' },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="yat-picker-checkout__sidebar-row">
                        <div className="yat-picker-checkout__sidebar-col">
                            <div className="yat-picker-checkout__sidebar-field">
                                <CardExpiryElement options={cardOption} />
                            </div>
                        </div>
                        <div className="yat-picker-checkout__sidebar-col">
                            <div className="yat-picker-checkout__sidebar-field">
                                <CardCvcElement options={cardOption} />
                            </div>
                        </div>
                    </div>
                    <div className="yat-picker-checkout__sidebar-tos">
                        <Checkbox checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} id="tos">
                            <span className="yat-picker-checkout__sidebar-tos__label">
                                I agree to the {/* eslint-disable react/jsx-no-target-blank */}
                                <a href={TERMS_LINK} target="_blank" rel="noreferrer">
                                    Terms of Service
                                </a>{' '}
                                {/* eslint-enable react/jsx-no-target-blank */}
                                and {/* eslint-disable react/jsx-no-target-blank */}
                                <a href={PRIVACY_LINK} target="_blank" rel="noreferrer">
                                    Privacy Policy
                                </a>
                                {/* eslint-enable react/jsx-no-target-blank */}
                            </span>
                        </Checkbox>
                    </div>
                    <DepButton
                        type="submit"
                        disabled={!stripe || !agreedToTerms}
                        size="large"
                        display={DepButtonDisplay.block}
                        classes="yat-picker-checkout__sidebar-pay"
                        loading={buttonLoading}
                    >
                        {t('buy_now_for_price', {
                            price: decimals(totalPrice),
                        })}
                    </DepButton>
                    <div className="yat-picker-checkout__sidebar-footer">{t('pay_once_own_forever')}</div>
                </form>
            </div>
        );
    }
    return <YatCryptoPaymentFlow onBack={handleBack} />;
};
